import {
    billingApi,
    type BillingOverview,
    type Invoice,
    type OverageStatus,
    type PaymentMethod,
    type Plan,
    type Subscription,
    type SubscriptionFromBalanceResponse,
    type Usage,
} from '@/api/billing'
import { useTeamStore } from '@/store/team'
import { getErrorMessage } from '@/utils/error-handling'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

// Re-export types for backward compatibility
export type { BillingOverview, Invoice, OverageStatus, PaymentMethod, Plan, Subscription, SubscriptionFromBalanceResponse, Usage }

// Legacy type for existing components
export interface BillingInfo {
  plan: {
    name: string
    price: number
    clicks: number
    renewalDate: string
  }
  usage: {
    clicks: { used: number; limit: number }
    domains: { used: number; limit: number }
    teamMembers: { used: number; limit: number }
    apiCalls: { used: number; limit: number }
  }
  paymentMethod: {
    type: 'card' | 'paypal'
    brand?: string
    last4?: string
    expMonth?: number
    expYear?: number
    email?: string
  } | null
}

// Query keys
export const billingKeys = {
  all: ['billing'] as const,
  overview: () => [...billingKeys.all, 'overview'] as const,
  plans: () => [...billingKeys.all, 'plans'] as const,
  subscription: () => [...billingKeys.all, 'subscription'] as const,
  usage: () => [...billingKeys.all, 'usage'] as const,
  paymentMethods: () => [...billingKeys.all, 'payment-methods'] as const,
  invoices: () => [...billingKeys.all, 'invoices'] as const,
  invoice: (id: string) => [...billingKeys.invoices(), id] as const,
  overage: () => [...billingKeys.all, 'overage'] as const,
  // Legacy keys for backward compatibility
  info: () => [...billingKeys.all, 'info'] as const,
}

// Hooks
export function useBillingOverview() {
  const currentTeamId = useTeamStore((s) => s.currentTeamId)
  return useQuery({
    queryKey: billingKeys.overview(),
    queryFn: () => billingApi.getOverview(),
    enabled: !!currentTeamId,
    staleTime: 60 * 1000, // 1 minute
  })
}

export function usePlans() {
  return useQuery({
    queryKey: billingKeys.plans(),
    queryFn: () => billingApi.getPlans(),
    staleTime: 5 * 60 * 1000, // Plans don't change often
  })
}

export function useSubscription() {
  const currentTeamId = useTeamStore((s) => s.currentTeamId)
  return useQuery({
    queryKey: billingKeys.subscription(),
    queryFn: () => billingApi.getSubscription(),
    enabled: !!currentTeamId,
    staleTime: 60 * 1000,
  })
}

export function useCreateSubscription() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (planId: string) => billingApi.createSubscription(planId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.all })
      toast.success('Subscription created successfully')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

/** Create subscription from internal USD balance — instant debit, no external checkout */
export function useCreateSubscriptionFromBalance() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ planId, billingInterval }: { planId: string; billingInterval?: 'month' | 'year' }) =>
      billingApi.createSubscriptionFromBalance(planId, billingInterval),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.all })
      queryClient.invalidateQueries({ queryKey: ['balance'] })
      toast.success('Subscription activated', {
        description: `Balance: $${(data.balanceAfterCents / 100).toFixed(2)}`,
      })
    },
    onError: (error) => {
      // Check for 402 Payment Required (insufficient balance)
      const msg = getErrorMessage(error)
      if (msg.includes('insufficient') || msg.includes('Insufficient')) {
        toast.error('Insufficient balance', {
          description: 'Please top up your balance first.',
        })
      } else {
        toast.error(msg)
      }
    },
  })
}

export function useUpdateSubscription() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (planId: string) => billingApi.updateSubscription(planId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.all })
      toast.success('Subscription updated successfully')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useCancelSubscription() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (options?: { atPeriodEnd?: boolean }) =>
      billingApi.cancelSubscription(options?.atPeriodEnd ? false : true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.all })
      toast.success('Subscription cancelled')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function usePaymentMethods() {
  return useQuery({
    queryKey: billingKeys.paymentMethods(),
    queryFn: () => billingApi.getPaymentMethods(),
    staleTime: 60 * 1000,
  })
}

export function useAddPaymentMethod() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => billingApi.addPaymentMethod(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.paymentMethods() })
      toast.success('Payment method added')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useRemovePaymentMethod() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (paymentMethodId: string) => billingApi.removePaymentMethod(paymentMethodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.paymentMethods() })
      toast.success('Payment method removed')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useSetDefaultPaymentMethod() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (paymentMethodId: string) => billingApi.setDefaultPaymentMethod(paymentMethodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.paymentMethods() })
      toast.success('Default payment method updated')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useInvoices(params?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: [...billingKeys.invoices(), params] as const,
    queryFn: () => billingApi.getInvoices(params),
    staleTime: 60 * 1000,
  })
}

export function useInvoice(id: string) {
  return useQuery({
    queryKey: billingKeys.invoice(id),
    queryFn: () => billingApi.getInvoice(id),
    enabled: !!id,
  })
}

export function useDownloadInvoice() {
  return useMutation({
    mutationFn: (id: string) => billingApi.downloadInvoice(id),
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useCreatePortalSession() {
  return useMutation({
    mutationFn: () => billingApi.createPortalSession(),
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useUsage() {
  return useQuery({
    queryKey: billingKeys.usage(),
    queryFn: async () => {
      const overview = await billingApi.getOverview()
      return overview.usage
    },
    staleTime: 30 * 1000, // 30 seconds for usage
    refetchInterval: 60 * 1000, // Refetch every minute
  })
}

export function useOverageStatus() {
  const currentTeamId = useTeamStore((s) => s.currentTeamId)
  return useQuery({
    queryKey: billingKeys.overage(),
    queryFn: () => billingApi.getOverageStatus(),
    enabled: !!currentTeamId,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  })
}

// Legacy hook for backward compatibility with existing components
export function useBillingInfo() {
  const plans = usePlans()

  return useQuery({
    queryKey: billingKeys.info(),
    queryFn: async (): Promise<BillingInfo> => {
      const overview = await billingApi.getOverview()
      const plan = plans.data?.find(p => p.id === overview.subscription?.planId)
      const defaultPayment = overview.paymentMethods.find(pm => pm.isDefault)

      return {
        plan: {
          name: overview.subscription?.planName || 'Free',
          price: plan?.price || 0,
          clicks: plan?.clicks || 0,
          renewalDate: overview.subscription?.currentPeriodEnd || '',
        },
        usage: overview.usage,
        paymentMethod: defaultPayment
          ? {
              type: defaultPayment.type,
              brand: defaultPayment.brand,
              last4: defaultPayment.last4,
              expMonth: defaultPayment.expMonth,
              expYear: defaultPayment.expYear,
              email: defaultPayment.email,
            }
          : null,
      }
    },
    staleTime: 60 * 1000,
  })
}

// Combined hook for billing page
export function useBilling() {
  const billingInfo = useBillingInfo()
  const invoices = useInvoices()

  return {
    billingInfo: billingInfo.data,
    invoices: invoices.data,
    isLoading: billingInfo.isLoading || invoices.isLoading,
    error: billingInfo.error || invoices.error,
  }
}
